<template>
  <AppLayout 
    page-title="Knowledge Hub" 
    page-subtitle="Access training materials, safety guidelines, and industry resources"
  >
    <!-- Knowledge Hub Content -->
    <div class="space-y-8 pt-8">
      <!-- Search and Filter Section -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div class="flex flex-col md:flex-row gap-4">
          <div class="flex-1">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search knowledge base..." 
              class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>
          <div class="flex gap-2">
            <select 
              v-model="selectedCategory" 
              class="px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            >
              <option value="">All Categories</option>
              <option value="safety">Safety Guidelines</option>
              <option value="training">Training Materials</option>
              <option value="procedures">Procedures</option>
              <option value="regulations">Regulations</option>
            </select>
            <button 
              @click="searchKnowledge"
              class="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <!-- Quick Access Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          v-for="card in quickAccessCards" 
          :key="card.id"
          class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group"
          @click="navigateToSection(card.route)"
        >
          <div class="flex items-center space-x-4">
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <component :is="card.icon" class="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                {{ card.title }}
              </h3>
              <p class="text-slate-400 text-sm">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Articles -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <h2 class="text-xl font-semibold text-white">Recent Articles</h2>
        </div>
        
        <div class="divide-y divide-slate-700/50">
          <div 
            v-for="article in recentArticles" 
            :key="article.id"
            class="p-6 hover:bg-slate-700/30 transition-colors cursor-pointer"
            @click="viewArticle(article)"
          >
            <div class="flex items-start space-x-4">
              <img :src="article.image" :alt="article.title" class="w-16 h-16 object-cover rounded-lg" />
              <div class="flex-1">
                <h3 class="text-lg font-medium text-white mb-2">{{ article.title }}</h3>
                <p class="text-slate-400 text-sm mb-3">{{ article.excerpt }}</p>
                <div class="flex items-center space-x-4 text-xs text-slate-500">
                  <span>{{ article.category }}</span>
                  <span>{{ formatDate(article.date) }}</span>
                  <span>{{ article.readTime }} min read</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Training Modules -->
      <div class="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div class="px-6 py-4 border-b border-slate-700/50">
          <h2 class="text-xl font-semibold text-white">Training Modules</h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
          <div 
            v-for="module in trainingModules" 
            :key="module.id"
            class="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group"
            @click="startTraining(module)"
          >
            <div class="aspect-video bg-gradient-to-br from-emerald-900/20 to-cyan-900/20 rounded-lg mb-4 relative overflow-hidden">
              <img :src="module.image" :alt="module.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div class="absolute top-3 right-3">
                <span class="px-2 py-1 bg-emerald-500/90 text-white text-xs font-medium rounded-full">
                  {{ module.duration }}
                </span>
              </div>
            </div>
            
            <h3 class="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
              {{ module.title }}
            </h3>
            <p class="text-slate-400 text-sm mb-3">{{ module.description }}</p>
            
            <div class="flex items-center justify-between">
              <span class="text-sm text-slate-400">{{ module.category }}</span>
              <span class="text-emerald-400 text-sm font-medium">Start →</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'

const router = useRouter()

// Reactive data
const searchQuery = ref('')
const selectedCategory = ref('')

// Quick access cards
const quickAccessCards = ref([
  {
    id: 1,
    title: 'Safety Guidelines',
    description: 'Essential safety procedures and best practices',
    icon: 'ShieldCheckIcon',
    route: '/safety'
  },
  {
    id: 2,
    title: 'Training Materials',
    description: 'Comprehensive training resources and courses',
    icon: 'AcademicCapIcon',
    route: '/store'
  },
  {
    id: 3,
    title: 'Procedures',
    description: 'Standard operating procedures and protocols',
    icon: 'ClipboardListIcon',
    route: '/procedures'
  },
  {
    id: 4,
    title: 'Regulations',
    description: 'Industry regulations and compliance requirements',
    icon: 'DocumentTextIcon',
    route: '/regulations'
  },
  {
    id: 5,
    title: 'AI Assistant',
    description: 'Get help with questions and guidance',
    icon: 'ChatBubbleLeftRightIcon',
    route: '/ai-messaging'
  },
  {
    id: 6,
    title: 'Support',
    description: 'Contact support and get help',
    icon: 'QuestionMarkCircleIcon',
    route: '/support'
  }
])

// Recent articles
const recentArticles = ref([
  {
    id: 1,
    title: 'New H2S Safety Protocols',
    excerpt: 'Updated safety protocols for hydrogen sulfide exposure in oilfield operations.',
    category: 'Safety',
    date: '2024-01-15',
    readTime: 5,
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop'
  },
  {
    id: 2,
    title: 'Confined Space Entry Guidelines',
    excerpt: 'Comprehensive guide for safe entry and work in confined spaces.',
    category: 'Procedures',
    date: '2024-01-12',
    readTime: 8,
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop'
  },
  {
    id: 3,
    title: 'Fall Protection Equipment Maintenance',
    excerpt: 'Proper maintenance and inspection procedures for fall protection equipment.',
    category: 'Training',
    date: '2024-01-10',
    readTime: 6,
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop'
  }
])

// Training modules
const trainingModules = ref([
  {
    id: 1,
    title: 'H2S Alive Certification',
    description: 'Comprehensive training on hydrogen sulfide safety and emergency response',
    category: 'Safety',
    duration: '8 hours',
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop'
  },
  {
    id: 2,
    title: 'First Aid & CPR',
    description: 'Essential first aid and CPR training for oilfield workers',
    category: 'Safety',
    duration: '6 hours',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop'
  },
  {
    id: 3,
    title: 'Confined Space Entry',
    description: 'Training for safe entry and work in confined spaces',
    category: 'Safety',
    duration: '4 hours',
    image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=300&fit=crop'
  }
])

// Methods
const searchKnowledge = () => {
  console.log('Searching knowledge base:', { query: searchQuery.value, category: selectedCategory.value })
}

const navigateToSection = (route: string) => {
  router.push(route)
}

const viewArticle = (article: any) => {
  console.log('Viewing article:', article.title)
  // Navigate to article detail page
}

const startTraining = (module: any) => {
  console.log('Starting training module:', module.title)
  // Navigate to training module
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}
</script>

<style scoped>
/* Custom scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.5) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.5);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.8);
}
</style>
